import {
  automaticRiskReason,
  CLIENT_TYPE_LABELS,
  CLIENT_TYPES,
  type Client,
  type ClientDocument,
  type ClientType,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES,
  DOSSIER_FIELD_LABELS,
  type DocumentExtraction,
  type DocumentType,
  type DossierField,
  decodeCurp,
  documentChecklist,
  dossierText,
  EXTRACTION_SOURCE_LABELS,
  ID_DOCUMENT_TYPE_LABELS,
  ID_DOCUMENT_TYPES,
  type IdDocumentType,
  isValidCurp,
  missingFields,
  REQUIRED_FIELDS_BY_CLIENT_TYPE,
  RISK_LEVEL_LABELS,
  RISK_LEVELS,
  RISK_REASON_LABELS,
  type RiskLevel,
} from "@pcj/shared";
import { CheckCircle2, Download, Eye, FolderOpen, X } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Banner, Button, CopyButton, Field, FileButton, inputClass, Modal, Pill } from "../../components/ui";
import { errorMessage, issueFor } from "../../lib/api";
import {
  DOCUMENT_ACCEPT,
  type ExtractableField,
  extractedFieldLabel,
  type FieldMerge,
  fileProblem,
  isPdf,
  mergeExtracted,
  READABLE_DOCUMENT_TYPES,
} from "../../lib/documents";
import { formatFileSize, plural } from "../../lib/format";
import { documentUrl } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";

interface FormState {
  name: string;
  type: ClientType;
  rfc: string;
  curp: string;
  riskLevel: RiskLevel;
  pep: boolean;
  birthDate: string;
  incorporationDate: string;
  address: string;
  phone: string;
  country: string;
  idDocumentType: IdDocumentType | "";
  idDocumentNumber: string;
  legalRepresentative: string;
  beneficialOwner: string;
  declaresBeneficialOwner: boolean;
}

const fromClient = (client: Client | null): FormState => ({
  name: client?.name ?? "",
  type: client?.type ?? "individual",
  rfc: client?.rfc ?? "",
  curp: client?.curp ?? "",
  riskLevel: client?.riskLevel ?? "medium",
  pep: client?.pep ?? false,
  birthDate: client?.birthDate ?? "",
  incorporationDate: client?.incorporationDate ?? "",
  address: client?.address ?? "",
  phone: client?.phone ?? "",
  country: client?.country ?? "México",
  idDocumentType: client?.idDocumentType ?? "",
  idDocumentNumber: client?.idDocumentNumber ?? "",
  legalRepresentative: client?.legalRepresentative ?? "",
  beneficialOwner: client?.beneficialOwner ?? "",
  declaresBeneficialOwner: client?.declaresBeneficialOwner ?? false,
});

const isPerson = (type: ClientType) => type === "individual" || type === "foreign_individual";
const COUNTRIES = ["México", "Estados Unidos", "Canadá", "Guatemala", "Colombia", "España", "China"];

/** A file chosen in the form; it is uploaded when the client is saved. */
interface PendingDocument {
  key: number;
  file: File;
  type: DocumentType;
  error?: string;
}

/** Outcome of reading a PDF, shown above the documents. */
type ReadResult =
  | { kind: "read"; source: NonNullable<DocumentExtraction["source"]>; filled: string[]; merge: FieldMerge }
  | { kind: "unreadable"; hasText: boolean; fileName: string };

/** Create or edit a client's identification dossier. `client === null` creates. */
export function ClientDialog({ open, client, onClose }: { open: boolean; client: Client | null; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title={client ? client.name : "Nuevo cliente"} wide>
      {/* Remount per client so the form starts from that client's data. */}
      {open && <ClientForm key={client?.id ?? "new"} client={client} onDone={onClose} />}
    </Modal>
  );
}

function ClientForm({ client, onDone }: { client: Client | null; onDone: () => void }) {
  const { actions, can, readOnly, company, companyId, today, documentsByClient, documentsEnabled } = useCompany();
  const [form, setForm] = useState<FormState>(() => fromClient(client));
  // After a create, the client exists: retrying a failed upload must not create it again.
  const [saved, setSaved] = useState<Client | null>(client);
  const [pending, setPending] = useState<PendingDocument[]>([]);
  const [otherType, setOtherType] = useState<DocumentType>("other");
  const [readResult, setReadResult] = useState<ReadResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const nextKey = useRef(1);
  const latestForm = useRef(form);
  useEffect(() => {
    latestForm.current = form;
  });

  const mutation = saved ? actions.updateClient : actions.createClient;
  const editable = can("capture") && !readOnly;
  const canSetRisk = can("manageCompliance");
  const uploaded = saved ? (documentsByClient.get(saved.id) ?? []) : [];

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key: keyof FormState) => (e: { target: { value: string } }) => set(key, e.target.value as never);

  const required = new Set<DossierField>(REQUIRED_FIELDS_BY_CLIENT_TYPE[form.type]);
  if (form.declaresBeneficialOwner) required.add("beneficialOwner");
  const draft = { ...form, idDocumentType: form.idDocumentType || null };
  const missing = missingFields(draft);
  const automatic = automaticRiskReason(draft);
  const person = isPerson(form.type);

  function setCurp(value: string) {
    const curp = value.trim().toUpperCase();
    setForm((f) => {
      // The CURP carries the birth date: fill it if it is still empty.
      const birthDate = !f.birthDate && isValidCurp(curp) ? (decodeCurp(curp)?.birthDate ?? "") : f.birthDate;
      return { ...f, curp: value, birthDate };
    });
  }

  function applyExtraction(extraction: DocumentExtraction, fileName: string) {
    if (!extraction.source) {
      setReadResult({ kind: "unreadable", hasText: extraction.hasText, fileName });
      return;
    }
    const current = latestForm.current;
    const next = { ...current };
    const filled: string[] = [];
    // A new client takes the person type the constancia states (individual or legal entity).
    const type = extraction.fields.type;
    if (!saved && type && type !== current.type && (current.type === "individual" || current.type === "legal_entity")) {
      next.type = type;
      filled.push("Tipo de cliente");
    }
    const merge = mergeExtracted(current, extraction.fields);
    for (const [field, value] of Object.entries(merge.fill) as [ExtractableField, string][]) {
      next[field] = value;
      filled.push(extractedFieldLabel(field));
    }
    setForm(next);
    setReadResult({ kind: "read", source: extraction.source, filled, merge });
  }

  function applyDocumentValues(merge: FieldMerge) {
    setForm((f) => ({ ...f, ...Object.fromEntries(merge.conflicts.map((c) => [c.field, c.value])) }));
    setReadResult((r) => (r?.kind === "read" ? { ...r, merge: { ...r.merge, conflicts: [] } } : r));
  }

  function addFile(file: File, type: DocumentType) {
    const problem = fileProblem(file);
    setFileError(problem);
    if (problem) return;
    setPending((list) => [...list, { key: nextKey.current++, file, type }]);
    if (READABLE_DOCUMENT_TYPES.includes(type) && isPdf(file)) {
      actions.extractDocument.mutate(file, { onSuccess: (extraction) => applyExtraction(extraction, file.name) });
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const { riskLevel, idDocumentType, ...rest } = form;
    const payload = {
      ...rest,
      // Only individuals have a CURP; a hidden field must not be saved.
      curp: person ? rest.curp : "",
      idDocumentType: idDocumentType || null,
      ...(canSetRisk ? { riskLevel } : {}),
    };
    setSaving(true);
    try {
      let target = saved;
      if (target) target = await actions.updateClient.mutateAsync({ id: target.id, ...payload });
      else {
        target = await actions.createClient.mutateAsync(payload);
        setSaved(target);
      }
      // Files one by one; the ones that fail stay in the list to retry.
      const failed: PendingDocument[] = [];
      for (const item of pending) {
        try {
          await actions.uploadDocument.mutateAsync({ clientId: target.id, file: item.file, type: item.type });
        } catch (error) {
          failed.push({ ...item, error: errorMessage(error) });
        }
      }
      setPending(failed);
      if (failed.length === 0) onDone();
    } catch {
      // The client was not saved: the mutation's error is shown below the form.
    } finally {
      setSaving(false);
    }
  }

  const label = (field: DossierField, fallback?: string) => fallback ?? DOSSIER_FIELD_LABELS[field];
  const error = mutation.error;
  const checklist = documentChecklist(form.type, [...uploaded, ...pending]);
  const checklistTypes = new Set(checklist.map((item) => item.type));

  return (
    <form onSubmit={submit} className="space-y-5">
      <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2">
        <Field
          label={label("name", person ? "Nombre completo" : "Denominación o razón social")}
          required
          error={issueFor(error, "name")}
          className="sm:col-span-2"
        >
          {(id) => <input id={id} required className={inputClass} value={form.name} onChange={text("name")} />}
        </Field>
        <Field label="Tipo de cliente" required>
          {(id) => (
            <select
              id={id}
              className={inputClass}
              value={form.type}
              onChange={(e) => set("type", e.target.value as ClientType)}
            >
              {CLIENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CLIENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="RFC" required={required.has("rfc")} error={issueFor(error, "rfc")}>
          {(id) => <input id={id} className={`${inputClass} uppercase`} value={form.rfc} onChange={text("rfc")} />}
        </Field>
        {person && (
          <Field
            label="CURP"
            error={issueFor(error, "curp")}
            hint="Con la CURP se llena la fecha de nacimiento."
            className="sm:col-span-2"
          >
            {(id) => (
              <input
                id={id}
                className={`${inputClass} uppercase`}
                maxLength={18}
                value={form.curp}
                onChange={(e) => setCurp(e.target.value)}
              />
            )}
          </Field>
        )}
        {person ? (
          <Field label="Fecha de nacimiento" required={required.has("birthDate")} error={issueFor(error, "birthDate")}>
            {(id) => (
              <input id={id} type="date" className={inputClass} value={form.birthDate} onChange={text("birthDate")} />
            )}
          </Field>
        ) : (
          <Field
            label="Fecha de constitución"
            required={required.has("incorporationDate")}
            error={issueFor(error, "incorporationDate")}
          >
            {(id) => (
              <input
                id={id}
                type="date"
                className={inputClass}
                value={form.incorporationDate}
                onChange={text("incorporationDate")}
              />
            )}
          </Field>
        )}
        <Field label={label("country")} required={required.has("country")}>
          {(id) => (
            <>
              <input id={id} list="countries" className={inputClass} value={form.country} onChange={text("country")} />
              <datalist id="countries">
                {COUNTRIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </>
          )}
        </Field>
        <Field label={label("address")} required={required.has("address")} className="sm:col-span-2">
          {(id) => <input id={id} className={inputClass} value={form.address} onChange={text("address")} />}
        </Field>
        <Field label={label("phone")} required={required.has("phone")}>
          {(id) => <input id={id} type="tel" className={inputClass} value={form.phone} onChange={text("phone")} />}
        </Field>
        {person ? (
          <>
            <Field label={label("idDocumentType")} required={required.has("idDocumentType")}>
              {(id) => (
                <select
                  id={id}
                  className={inputClass}
                  value={form.idDocumentType}
                  onChange={(e) => set("idDocumentType", e.target.value as IdDocumentType | "")}
                >
                  <option value="">Selecciona…</option>
                  {ID_DOCUMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ID_DOCUMENT_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={label("idDocumentNumber")} required={required.has("idDocumentNumber")}>
              {(id) => (
                <input
                  id={id}
                  className={inputClass}
                  value={form.idDocumentNumber}
                  onChange={text("idDocumentNumber")}
                />
              )}
            </Field>
          </>
        ) : (
          <Field
            label={label(
              "legalRepresentative",
              form.type === "trust" ? "Delegado fiduciario o representante" : undefined,
            )}
            required={required.has("legalRepresentative")}
          >
            {(id) => (
              <input
                id={id}
                className={inputClass}
                value={form.legalRepresentative}
                onChange={text("legalRepresentative")}
              />
            )}
          </Field>
        )}

        <div className="space-y-2 sm:col-span-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.pep} onChange={(e) => set("pep", e.target.checked)} />
            Es o ha sido Persona Políticamente Expuesta (PPE)
          </label>
          {person && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.declaresBeneficialOwner}
                onChange={(e) => set("declaresBeneficialOwner", e.target.checked)}
              />
              Declara que actúa por cuenta de un Beneficiario Controlador distinto
            </label>
          )}
        </div>
        {(required.has("beneficialOwner") || form.beneficialOwner) && (
          <Field
            label={label("beneficialOwner")}
            required={required.has("beneficialOwner")}
            className="sm:col-span-2"
            hint="Nombre completo; para personas morales, quien ejerce el control o es dueño del 25% o más."
          >
            {(id) => (
              <input id={id} className={inputClass} value={form.beneficialOwner} onChange={text("beneficialOwner")} />
            )}
          </Field>
        )}

        <Field
          label="Grado de Riesgo"
          hint={
            canSetRisk
              ? "Bajo / medio / alto, a criterio del Responsable de Cumplimiento."
              : "Lo asigna el Responsable de Cumplimiento."
          }
        >
          {(id) => (
            <select
              id={id}
              className={inputClass}
              value={automatic ? "high" : form.riskLevel}
              disabled={!canSetRisk || !!automatic}
              onChange={(e) => set("riskLevel", e.target.value as RiskLevel)}
            >
              {RISK_LEVELS.map((r) => (
                <option key={r} value={r}>
                  {RISK_LEVEL_LABELS[r]}
                </option>
              ))}
            </select>
          )}
        </Field>
      </fieldset>

      <section className="space-y-3 rounded-lg border border-gray-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Documentos del expediente</h3>
            {editable && documentsEnabled && (
              <p className="text-xs text-gray-500">
                Se suben al guardar. De la Constancia de Situación Fiscal y la constancia de la CURP en PDF (descargadas
                del SAT y de gob.mx) se llenan los datos automáticamente.
              </p>
            )}
          </div>
          {saved && (uploaded.length > 0 || documentsEnabled) && (
            <Link
              to={`/companies/${companyId}/clients/${saved.id}/documents`}
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 underline"
            >
              <FolderOpen className="h-4 w-4" aria-hidden /> Ver documentos
            </Link>
          )}
        </div>

        {!documentsEnabled && (
          <p className="text-xs text-gray-500">La carga de documentos no está disponible en este servidor.</p>
        )}

        {readResult?.kind === "read" && (
          <Banner tone="success" title={`Datos tomados de ${EXTRACTION_SOURCE_LABELS[readResult.source]}`}>
            {readResult.filled.length > 0 ? (
              <p>Se llenaron: {readResult.filled.join(", ")}. Revísalos antes de guardar.</p>
            ) : (
              <p>Los datos del documento coinciden con los capturados.</p>
            )}
            {readResult.merge.conflicts.length > 0 && (
              <div className="mt-2">
                <p>El documento indica otros datos:</p>
                <ul className="list-disc pl-5">
                  {readResult.merge.conflicts.map((c) => (
                    <li key={c.field}>
                      {extractedFieldLabel(c.field)}: <strong>{c.value}</strong> (capturado: {c.current})
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => applyDocumentValues(readResult.merge)}
                >
                  Usar los datos del documento
                </Button>
              </div>
            )}
          </Banner>
        )}
        {readResult?.kind === "unreadable" && (
          <Banner tone="info" title={`No se pudieron leer datos de ${readResult.fileName}`}>
            {readResult.hasText
              ? "No parece una Constancia de Situación Fiscal ni una constancia de la CURP. El archivo se guardará igual; captura los datos a mano."
              : "El PDF no tiene texto (parece un escaneo o una foto). El archivo se guardará igual; captura los datos a mano."}
          </Banner>
        )}
        {actions.extractDocument.isPending && <p className="text-xs text-gray-500">Leyendo el documento…</p>}
        {fileError && <Banner tone="error">{fileError}</Banner>}

        <ul className="divide-y divide-gray-100 text-sm">
          {checklist.map((item) => {
            const done = uploaded.filter((d) => d.type === item.type);
            const queued = pending.filter((p) => p.type === item.type);
            return (
              <li key={item.type} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-800">{item.label}</p>
                  <DocumentList documents={done} companyId={companyId} />
                  <PendingList items={queued} onRemove={(key) => setPending((l) => l.filter((p) => p.key !== key))} />
                </div>
                <div className="flex items-center gap-2">
                  {done.length > 0 ? (
                    <Pill tone="green" icon={CheckCircle2}>
                      {plural(done.length, "archivo", "archivos")}
                    </Pill>
                  ) : queued.length === 0 ? (
                    <Pill>Pendiente</Pill>
                  ) : null}
                  {editable && documentsEnabled && (
                    <FileButton
                      accept={DOCUMENT_ACCEPT}
                      label={`Archivo: ${DOCUMENT_TYPE_LABELS[item.type]}`}
                      onFile={(file) => addFile(file, item.type)}
                    >
                      Subir
                    </FileButton>
                  )}
                </div>
              </li>
            );
          })}
          <li className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-gray-800">Otros documentos</p>
              <DocumentList
                documents={uploaded.filter((d) => !checklistTypes.has(d.type))}
                companyId={companyId}
                showType
              />
              <PendingList
                items={pending.filter((p) => !checklistTypes.has(p.type))}
                onRemove={(key) => setPending((l) => l.filter((p) => p.key !== key))}
                showType
              />
            </div>
            {editable && documentsEnabled && (
              <div className="flex items-center gap-2">
                <select
                  aria-label="Tipo del otro documento"
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs"
                  value={otherType}
                  onChange={(e) => setOtherType(e.target.value as DocumentType)}
                >
                  {DOCUMENT_TYPES.filter((t) => !checklistTypes.has(t)).map((t) => (
                    <option key={t} value={t}>
                      {DOCUMENT_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <FileButton
                  accept={DOCUMENT_ACCEPT}
                  label="Archivo: otro documento"
                  onFile={(file) => addFile(file, checklistTypes.has(otherType) ? "other" : otherType)}
                >
                  Subir
                </FileButton>
              </div>
            )}
          </li>
        </ul>
      </section>

      {automatic && (
        <Banner tone="warning" title="Riesgo alto automático">
          {RISK_REASON_LABELS[automatic]}: requiere debida diligencia reforzada.
        </Banner>
      )}
      {missing.length > 0 ? (
        <Banner
          tone="info"
          title={`Falta${missing.length === 1 ? "" : "n"} ${plural(missing.length, "dato", "datos")} del expediente`}
        >
          {missing.map((f) => DOSSIER_FIELD_LABELS[f]).join(", ")}. Son obligatorios cuando el cliente alcanza el umbral
          de identificación.
        </Banner>
      ) : (
        <Banner tone="success">Expediente completo.</Banner>
      )}

      {saved && (
        <details className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Expediente para imprimir o copiar
          </summary>
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              <CopyButton text={dossierText(saved, company, today, uploaded)} label="Copiar expediente" />
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs text-gray-700">
              {dossierText(saved, company, today, uploaded)}
            </pre>
          </div>
        </details>
      )}

      {error && <Banner tone="error">{errorMessage(error)}</Banner>}
      {pending.some((p) => p.error) && (
        <Banner tone="error" title="Algunos documentos no se subieron">
          Los datos del cliente sí se guardaron. Revisa los archivos marcados y vuelve a guardar para reintentar.
        </Banner>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          {editable ? "Cancelar" : "Cerrar"}
        </Button>
        {editable && (
          <Button type="submit" loading={saving}>
            {saved ? "Guardar cambios" : "Guardar cliente"}
          </Button>
        )}
      </div>
    </form>
  );
}

/** Uploaded files of a checklist item, with view and download links. */
function DocumentList({
  documents,
  companyId,
  showType = false,
}: {
  documents: readonly ClientDocument[];
  companyId: string;
  showType?: boolean;
}) {
  if (documents.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {documents.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          <span className="truncate">
            {showType ? `${DOCUMENT_TYPE_LABELS[d.type]} · ` : ""}
            {d.fileName} · {formatFileSize(d.sizeBytes)}
          </span>
          <Link
            to={`/companies/${companyId}/clients/${d.clientId}/documents?document=${d.id}`}
            className="inline-flex items-center gap-0.5 text-gray-700 underline"
          >
            <Eye className="h-3 w-3" aria-hidden /> Ver
          </Link>
          <a href={documentUrl(companyId, d.id, true)} download className="inline-flex items-center gap-0.5 underline">
            <Download className="h-3 w-3" aria-hidden /> Descargar
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Files chosen in the form and not uploaded yet. */
function PendingList({
  items,
  onRemove,
  showType = false,
}: {
  items: readonly PendingDocument[];
  onRemove: (key: number) => void;
  showType?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {items.map((item) => (
        <li key={item.key} className="flex flex-wrap items-center gap-x-2 text-xs">
          <span className={item.error ? "text-rose-700" : "text-amber-800"}>
            {showType ? `${DOCUMENT_TYPE_LABELS[item.type]} · ` : ""}
            {item.file.name} · {formatFileSize(item.file.size)} · {item.error ?? "por subir"}
          </span>
          <button
            type="button"
            onClick={() => onRemove(item.key)}
            className="inline-flex items-center text-gray-400 hover:text-gray-700"
            aria-label={`Quitar ${item.file.name}`}
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
